import { Alert, Box, Button, Typography } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import { RefreshCwIcon } from 'lucide-react';

interface SentryErrorBoundaryProps {
  children: React.ReactNode;
  fallback?:
    | React.ReactElement
    | ((errorData: {
        error: unknown;
        componentStack: string;
        eventId: string;
        resetError(): void;
      }) => React.ReactElement);
}

const DefaultErrorFallback = ({
  error,
  resetError,
}: {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError(): void;
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
        textAlign: 'center',
      }}
    >
      <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
        <Typography variant="h6" gutterBottom>
          Something went wrong
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          We're sorry, but something unexpected happened. Our team has been
          notified.
        </Typography>
        {import.meta.env.DEV && (
          <Typography
            variant="caption"
            component="pre"
            sx={{ mt: 2, textAlign: 'left' }}
          >
            {error instanceof Error ? error.message : String(error)}
          </Typography>
        )}
      </Alert>
      <Button
        variant="contained"
        startIcon={<RefreshCwIcon size={16} />}
        onClick={resetError}
        sx={{ mt: 2 }}
      >
        Try again
      </Button>
    </Box>
  );
};

export const SentryErrorBoundary = ({
  children,
  fallback: Fallback = DefaultErrorFallback,
}: SentryErrorBoundaryProps) => {
  return (
    <ErrorBoundary
      fallback={Fallback}
      beforeCapture={(scope, _error, componentStack) => {
        // Add additional context before capturing the error
        scope.setTag('errorBoundary', true);
        scope.setContext('componentStack', { stack: componentStack });
        scope.setLevel('error');
      }}
      showDialog={false}
    >
      {children}
    </ErrorBoundary>
  );
};
